import React, { useEffect, useState } from 'react';
import { Spin, Alert } from 'antd';
import { publicIpCheckApi } from '../services/publicIpCheckApi';

interface IpWhitelistGuardProps {
  children: React.ReactNode;
}

const IpWhitelistGuard: React.FC<IpWhitelistGuardProps> = ({ children }) => {
  const [isChecking, setIsChecking] = useState(true);
  const [isWhitelisted, setIsWhitelisted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkIp = async () => {
      try {
        // First check if whitelist is enabled
        const statusResponse = await publicIpCheckApi.getWhitelistStatus();
        console.log('Whitelist status response:', statusResponse);
        const isWhitelistEnabled = (statusResponse as any).data?.enabled;
        
        if (!isWhitelistEnabled) {
          setIsWhitelisted(true);
          return;
        }
        
        const response = await publicIpCheckApi.checkIp();
        
        if ((response as any).data?.isWhitelisted) {
          setIsWhitelisted(true);
        } else {
          window.location.href = '/404';
        }
      } catch (error: any) {
        
        // For connection errors, allow access (don't redirect)
        if (error.code === 'ERR_CONNECTION_RESET' || error.code === 'ERR_NETWORK') {
          setIsWhitelisted(true);
          return;
        }
        
        setError('Failed to check IP whitelist. Please try again.');
        
        // If it's a 401 (unauthorized), it might be an API key issue
        if (error.response?.status === 401) {
          setError('API key error. Please contact administrator.');
        }
        // If it's a 403 (forbidden), IP is not whitelisted
        else if (error.response?.status === 403) {
          window.location.href = '/404';
        }
        // For other errors, show error message
        else {
          setError('Network error. Please check your connection.');
        }
      } finally {
        setIsChecking(false);
      }
    };

    checkIp();
  }, []);

  if (isChecking) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <Spin size="large" />
        <div>Checking IP whitelist...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: '16px',
        padding: '20px'
      }}>
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ maxWidth: 500 }}
        />
        <button 
          onClick={() => window.location.reload()}
          style={{
            padding: '8px 16px',
            backgroundColor: '#1890ff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!isWhitelisted) {
    return null; // Will redirect to access-denied
  }

  return <>{children}</>;
};

export default IpWhitelistGuard;
